import { useState } from 'react'

import { EditDriverModal } from '@/components/DriversTable'
import { EditEquipmentModal } from '@/components/EquipmentTable'
import { trailerConfig } from '@/components/TrailersTable'
import { truckConfig } from '@/components/TrucksTable'
import { Field, FieldLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { type Driver, formatDriverDates } from '@/models/driver'
import type { Equipment } from '@/models/equipment'
import type { EditChangeProps } from './EditChange'
import { changedFields, DRIVER_FIELDS, VEHICLE_FIELDS } from './EditUnitForm'

/** A requested correction to a unit on the policies: the roster's form, showing the details as corrected. */
export function EditCorrection({
  line,
  trucks,
  trailers,
  drivers,
  onSave,
  onClose,
}: Omit<EditChangeProps, 'coverages'>) {
  const [description, setDescription] = useState(line.description)
  const unitId = line.unit?.id
  const note = (
    <Field>
      <FieldLabel htmlFor={`${line.id}-description`} optional>
        Anything else we should know
      </FieldLabel>
      <Textarea
        id={`${line.id}-description`}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
    </Field>
  )
  if (line.unit?.type === 'driver') {
    const live = drivers.find((candidate) => candidate.id === unitId)
    if (!live) return null
    const corrected: Driver = { ...live, ...live.pendingChange, id: live.id }
    return (
      <EditDriverModal
        driver={corrected}
        existingDrivers={drivers.filter((other) => other.id !== unitId)}
        onClose={onClose}
        onSubmit={(edited) =>
          onSave({
            unit: { ...formatDriverDates(changedFields(corrected, edited, DRIVER_FIELDS)) },
            description,
          })
        }
        enforceCarrierRequired
        hideOwnerToggle
      >
        {note}
      </EditDriverModal>
    )
  }
  const vehicles = line.unit?.type === 'trailer' ? trailers : trucks
  const live = vehicles.find((candidate) => candidate.id === unitId)
  if (!live) return null
  const pending = live.pendingChange
  const corrected: Equipment = pending
    ? { ...live, ...pending, year: pending.year ?? undefined, id: live.id }
    : live
  return (
    <EditEquipmentModal
      item={corrected}
      isNew={false}
      existingItems={vehicles.filter((other) => other.id !== unitId)}
      onClose={onClose}
      onSubmit={(edited) =>
        onSave({ unit: { ...changedFields(corrected, edited, VEHICLE_FIELDS) }, description })
      }
      config={line.unit?.type === 'trailer' ? trailerConfig : truckConfig}
      enforceCarrierRequired
    >
      {note}
    </EditEquipmentModal>
  )
}
